import { Pokemon } from "prisma-layer";
import { useMemo } from "react";
import { POKEMON_SKILLS } from "game/data/skills/POKEMON_SKILLS";
import { getPokeData } from "game/data/pokemon/evolution";
import { MiniSkillCard } from "@/components/pokemon/mini-skill-card";
import { PokemonTalent } from "@/components/pokemon/pokemon-talent";
import { PokemonTypeBadge } from "@/components/pokemon/pokemon-type-badge";
import { SegmentedProgressBar } from "@/components/ui/progress/segmented-progress-bar";
import { getPokemonStats } from "@/utils/pokemon-stats-utils";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { PokemonPortrait } from "@/components/pokemon/pokemon-portrait";
import { PokeType } from "types/pokemon/poke-type";
import { isXsScreen } from "@/utils/screen-size-check";
import { calculatePurity } from "@/utils/talent-utils";
import { StrokedText } from "../ui/text/stroked-text";

interface PokemonPreviewBoxProps {
  pokemon: Pick<
    Pokemon,
    | "id"
    | "display_id"
    | "base_pokemon_name"
    | "level"
    | "talents"
    | "active_skills"
    | "is_shiny"
  >;
}

export const PokemonPreviewBox = ({ pokemon }: PokemonPreviewBoxProps) => {
  const stats = useMemo(() => {
    // Force level 1 to avoid level scaling in stats calculation
    return getPokemonStats({ ...pokemon, level: 1 });
  }, [pokemon]);

  const skills = useMemo(() => {
    return pokemon.active_skills.map((skillId) => POKEMON_SKILLS[skillId]);
  }, [pokemon.active_skills]);

  const pokeData = useMemo(
    () => getPokeData(pokemon.base_pokemon_name),
    [pokemon.base_pokemon_name],
  );

  if (!stats) return null;

  return (
    <div className="relative flex flex-col w-full animate-fadeIn">
      <div className="flex gap-4">
        {/* Portrait - Fixed width column */}
        <div className="flex flex-col flex-shrink-0 items-center gap-2 w-12 xs:w-24">
          <div className="group hidden xs:block relative bg-pk-night-3 border border-white/10 rounded-xl w-24 h-24 overflow-hidden">
            <PokemonPortrait
              pokemon={pokemon}
              className="p-1 w-full h-full object-contain"
            />
          </div>
          <div className="bg-pk-garnet-2 shadow-sm px-2 py-0.5 border border-pk-garnet-4 rounded">
            <span className="font-bold text-[10px] text-pk-gray-5">
              #{pokemon.display_id.slice(0, 6)}
            </span>
          </div>
          {pokeData?.types && (
            <div className="flex xs:flex-row flex-col flex-wrap justify-center gap-1">
              {pokeData.types.filter(Boolean).map((t) => (
                <PokemonTypeBadge
                  key={t}
                  type={t as PokeType}
                  iconSize={12}
                  className="px-1.5 py-0.5 text-[9px]"
                  hideText={isXsScreen()}
                />
              ))}
            </div>
          )}
        </div>

        {/* Content column */}
        <div className="flex flex-col flex-grow gap-3">
          <div className="flex justify-between items-center pb-1 border-white/10 border-b">
            <h3 className="font-bold text-pk-gray-5 text-xs uppercase">
              {pokemon.base_pokemon_name}
            </h3>
          </div>

          <div className="gap-4 grid grid-cols-2">
            {/* Stats Column */}
            <div className="flex flex-col gap-2">
              <div className="space-y-0.5">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-[9px] text-pk-gray-4 uppercase">
                    Ability Power
                  </span>
                  <span className="font-bold text-pk-gray-5 text-sm">
                    {stats.ability_power}
                  </span>
                </div>
                <div className="hidden xs:block">
                  <SegmentedProgressBar
                    ratio={Math.min(stats.ability_power / 300, 1)}
                    variant="red"
                    segmented={false}
                    className="p-0.5 border-2 h-2"
                    maxSquares={10}
                  />
                </div>
              </div>

              <div className="space-y-0.5">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-[9px] text-pk-gray-4 uppercase">
                    Resistance
                  </span>
                  <span className="font-bold text-pk-gray-5 text-sm">
                    {stats.resistance}
                  </span>
                </div>
                <div className="hidden xs:block">
                  <SegmentedProgressBar
                    ratio={Math.min(stats.resistance / 300, 1)}
                    variant="blue"
                    segmented={false}
                    className="p-0.5 border-2 h-2"
                    maxSquares={10}
                  />
                </div>
              </div>

              <div className="space-y-0.5">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-[9px] text-pk-gray-4 uppercase">
                    Dexterity
                  </span>
                  <span className="font-bold text-pk-gray-5 text-sm">
                    {stats.speed}
                  </span>
                </div>
                <div className="hidden xs:block">
                  <SegmentedProgressBar
                    ratio={Math.min(stats.speed / 300, 1)}
                    variant="yellow"
                    segmented={false}
                    maxSquares={10}
                  />
                </div>
              </div>
            </div>

            {/* Talents Column */}
            <div className="flex flex-col gap-1">
              <span className="pl-0.5 font-bold text-[9px] text-pk-gray-4 uppercase">
                {isXsScreen() ? "Purity" : "Talents"}
              </span>
              {isXsScreen() ? (
                <StrokedText
                  strokeWidth={9}
                  fontSize={18}
                  fillColor="#f4eae7"
                  strokeColor="#232020"
                  className="text-center"
                >
                  {calculatePurity(
                    pokemon.talents,
                    (pokeData?.types ?? []) as PokeType[],
                  ).toString()}
                  /9
                </StrokedText>
              ) : (
                <div className="gap-1 grid grid-cols-3 bg-black/20 p-1.5 border border-white/5 rounded-lg">
                  {pokemon.talents.map((talent, i) => (
                    <PokemonTalent
                      key={i}
                      talent={String(talent)}
                      width={16}
                      height={20}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Skills Row */}
      <div className="flex flex-col gap-1.5 mt-3 pt-3 border-white/5 border-t">
        <span className="pl-0.5 font-bold text-[9px] text-pk-gray-4 uppercase">
          Active Skills
        </span>
        <div className="gap-2 grid grid-cols-3">
          {skills.map(
            (skill, i) =>
              skill && (
                <div key={i} className="h-20">
                  <MiniSkillCard skill={skill} />
                </div>
              ),
          )}
        </div>
      </div>
    </div>
  );
};
